// @ts-nocheck
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block";
import { orderProps } from "@store/Themes/common/props/order";
import { getCarrierInfo } from "@store/Framework/Store/Store/Pages/CheckoutShipping/CheckoutShippingSelectors";
import setCarrierInfo from "@store/Framework/Store/Store/Pages/CheckoutShipping/Handlers/SetCarrierInfo";
import { CheckoutShippingFormContext } from "@store/Overrides/Pages/CheckoutShippingPage";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    isCollectRequired = "isCollectRequired",
    isCollectNumberRequired = "isCollectNumberRequired",
    carrierCodeForBillToDefault = "carrierCodeForBillToDefault",
    carrierCodeForShipToDefault = "carrierCodeForShipToDefault",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.isCollectRequired]: boolean;
        [fields.isCollectNumberRequired]: boolean;
        [fields.carrierCodeForBillToDefault]: string;
        [fields.carrierCodeForShipToDefault]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { isUpdatingCart } = state.pages.checkoutShipping;
    const settingsCollection = getSettingsCollection(state);
    const carrierCodeCollectCourrier =
        settingsCollection.extensionCartSettings.carrierCodeCollectCourrier?.split(",") ?? [];
    const carrierInfo = getCarrierInfo(state);

    const cart = cartState.value;
    const carrierOptions = cart?.properties.carrierOptions ? JSON.parse(cart?.properties.carrierOptions) : [];

    return {
        cart,
        carrierOptions,
        isLoading: !cartState.value || cartState.isLoading || isUpdatingCart,
        cartBillTo: getBillToState(state, cartState.value?.billToId)?.value,
        carrierCodeCollectCourrier,
        cartShipTo: getShipToState(state, cartState.value?.shipToId)?.value,
        carrierCode: carrierInfo?.carrierCode,
        collect: carrierInfo?.collect,
        collectNumber: carrierInfo?.collectNumber,
        isGuestOrder: cart?.isGuestOrder ?? false,
    };
};

const mapDispatchToProps = {
    setCarrierInfo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutShippingCarrierSelectorStyles {
    headingText?: TypographyPresentationProps;
    block?: BlockProps;
    container?: GridContainerProps;
    gridItemCarrier?: GridItemProps;
    gridItemCodeAndCollectNumber?: GridItemProps;
    carrierSelect?: SelectPresentationProps;
    carrierCourrierInput?: TextFieldPresentationProps;
    carrierCourrierNumberInput?: TextFieldPresentationProps;
}

export const checkoutShippingCarrierStyles: CheckoutShippingCarrierSelectorStyles = {
    block: orderProps.block,
    headingText: orderProps.subtitle,
    gridItemCarrier: {
        width: [12, 6, 6, 6, 6],
    },
    gridItemCodeAndCollectNumber: {
        width: [12, 6, 6, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    carrierCourrierNumberInput: {
        cssOverrides: {
            formField: css`
                margin-top: ${pxToRem(10)};
            `,
        },
    },
};

const styles = checkoutShippingCarrierStyles;

const CheckoutShippingCarrierSelector: React.FC<Props> = ({
    cart,
    carrierOptions,
    isLoading,
    cartBillTo,
    cartShipTo,
    id,
    setCarrierInfo,
    fields,
    carrierCodeCollectCourrier,
    carrierCode,
    collect,
    collectNumber,
    isGuestOrder,
}) => {
    const { carrierCodeForBillToDefault, carrierCodeForShipToDefault, isCollectRequired, isCollectNumberRequired } =
        fields;
    const [useCollect, setUseCollect] = useState(false);

    const [collectErrorMessage, setCollectErrorMessage] = useState("");
    const [collectNumberErrorMessage, setCollectNumberErrorMessage] = useState("");

    const checkoutShippingFormContext = useContext(CheckoutShippingFormContext);

    useEffect(() => {
        checkoutShippingFormContext.fieldValidators.collect = validateCollectCourrier;
        if (typeof collect !== "undefined") {
            validateCollectCourrier();
        }
    }, [collect]);

    useEffect(() => {
        checkoutShippingFormContext.fieldValidators.collect = validateCollectCourrier;
    }, [isCollectRequired, useCollect]);

    const validateCollectCourrier = () => {
        const errorMessage =
            isCollectRequired && !collect && useCollect
                ? (siteMessage("Field_Required", translate("Collect Courrier")) as string)
                : "";
        setCollectErrorMessage(errorMessage);
        return !errorMessage;
    };

    useEffect(() => {
        checkoutShippingFormContext.fieldValidators.collectNumber = validateCollectCourrierNumber;
        if (typeof collectNumber !== "undefined") {
            validateCollectCourrierNumber();
        }
    }, [collectNumber]);

    useEffect(() => {
        checkoutShippingFormContext.fieldValidators.collectNumber = validateCollectCourrierNumber;
    }, [isCollectNumberRequired, useCollect]);

    const validateCollectCourrierNumber = () => {
        const errorMessage =
            isCollectNumberRequired && !collectNumber && useCollect
                ? (siteMessage("Field_Required", translate("Collect Courrier Number")) as string)
                : "";
        setCollectNumberErrorMessage(errorMessage);
        return !errorMessage;
    };

    useEffect(() => {
        const _useCollect =
            carrierCode !== undefined && carrierCode !== "" && carrierCodeCollectCourrier.includes(carrierCode);
        setUseCollect(_useCollect);
    }, [carrierCode]);

    useEffect(() => {
        // Set the values coming from the cart's custom props if they exist
        if (cart?.properties) {
            let setCarrierCode = carrierCode;
            let setCollect = collect;
            let setCollectNumber = collectNumber;

            if (cart.properties.carrierCode) {
                setCarrierCode = cart.properties.carrierCode;
            } else if (carrierOptions && carrierOptions.length > 0) {
                setCarrierCode = carrierOptions[0].CarrierCode;
            }

            if (cart.properties.collectCourrier) {
                setCollect = cart.properties.collectCourrier;
            } else if (setCarrierCode) {
                setCollect = getCourrierBasedOnCode(setCarrierCode, setCollect);
            }

            if (cart.properties.collectCourrierNumber) {
                setCollectNumber = cart.properties.collectCourrierNumber;
            } else if (setCarrierCode) {
                setCollectNumber = getCourrierNumberBasedOnCode(setCarrierCode, setCollectNumber);
            }

            setCarrierInfo({
                carrierInfo: {
                    carrierCode: setCarrierCode,
                    collect: setCollect,
                    collectNumber: setCollectNumber,
                },
            });
        }
    }, [cart]);

    const handleCarrierChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const setCarrierCode = event.currentTarget.value;
        let setCollect = collect;
        let setCollectNumber = collectNumber;

        setCollect = getCourrierBasedOnCode(setCarrierCode, setCollect);
        setCollectNumber = getCourrierNumberBasedOnCode(setCarrierCode, setCollectNumber);

        setCarrierInfo({
            carrierInfo: {
                carrierCode: setCarrierCode,
                collect: setCollect,
                collectNumber: setCollectNumber,
            },
        });
    };
    const getCourrierBasedOnCode = (_carrierCode: string, _oldCollect: string | undefined) => {
        if (_carrierCode === carrierCodeForBillToDefault) {
            if (cartBillTo?.properties?.collectCourrier) {
                return cartBillTo.properties.collectCourrier;
            }
        } else if (_carrierCode === carrierCodeForShipToDefault) {
            if (cartShipTo?.properties?.collectCourrier) {
                return cartShipTo.properties.collectCourrier;
            }
        }
        return _oldCollect;
    };
    const getCourrierNumberBasedOnCode = (_carrierCode: string, _oldCollectNumber: string | undefined) => {
        if (_carrierCode === carrierCodeForBillToDefault) {
            if (cartBillTo?.properties?.collectCourrierNumber) {
                return cartBillTo.properties.collectCourrierNumber;
            }
        } else if (_carrierCode === carrierCodeForShipToDefault) {
            if (cartShipTo?.properties?.collectCourrierNumber) {
                return cartShipTo.properties.collectCourrierNumber;
            }
        }
        return _oldCollectNumber;
    };

    const handleCollectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCarrierInfo({
            carrierInfo: {
                carrierCode,
                collect: event.currentTarget.value,
                collectNumber,
            },
        });
    };
    const handleCollectNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCarrierInfo({
            carrierInfo: {
                carrierCode,
                collect,
                collectNumber: event.currentTarget.value,
            },
        });
    };

    if (!cart || !cart.properties || !cart.properties.carrierOptions) {
        return null;
    }

    return (
        <Block {...styles.block}>
            <Typography {...styles.headingText}>{translate("Carrier")}</Typography>
            <GridContainer {...styles.container}>
                <GridItem {...styles.gridItemCarrier}>
                    <Select
                        {...styles.carrierSelect}
                        label={translate("Carrier Option")}
                        value={carrierCode}
                        required={true}
                        onChange={handleCarrierChange}
                        disabled={isGuestOrder}
                        data-test-selector="customerAddressForm_country"
                    >
                        {carrierOptions?.map((co: any) => (
                            <option key={co.CarrierCode} value={co.CarrierCode}>
                                {co.Label}
                            </option>
                        ))}
                    </Select>
                </GridItem>
                {useCollect && (
                    <>
                        <GridItem {...styles.gridItemCodeAndCollectNumber}>
                            <TextField
                                {...styles.carrierCourrierInput}
                                label={translate("Collect Courrier")}
                                value={collect || ""}
                                required={true}
                                onChange={handleCollectChange}
                                error={collectErrorMessage}
                            />
                            <TextField
                                {...styles.carrierCourrierNumberInput}
                                label={translate("Collect Courrier Number")}
                                value={collectNumber || ""}
                                required={true}
                                onChange={handleCollectNumberChange}
                                error={collectNumberErrorMessage}
                            />
                        </GridItem>
                    </>
                )}
            </GridContainer>
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutShippingCarrierSelector),
    definition: {
        group: "Checkout - Shipping",
        displayName: "Carrier Selector",
        allowedContexts: ["CheckoutShippingPage"],
        fieldDefinitions: [
            {
                name: fields.isCollectRequired,
                displayName: "Courrier Collect required",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.isCollectNumberRequired,
                displayName: "Courrier Collect Number required",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.carrierCodeForBillToDefault,
                displayName: "Carrier Code for BillTo Values",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip:
                    "When this carrier code is selected from the list, default values for collect and collectNumber will be used from the BillTo.",
                sortOrder: 2,
            },
            {
                name: fields.carrierCodeForShipToDefault,
                displayName: "Carrier Code for ShipTo Values",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip:
                    "When this carrier code is selected from the list, default values for collect and collectNumber will be used from the ShipTo.",
                sortOrder: 3,
            },
        ],
    },
};

export default widgetModule;
